import type { LucideIcon } from 'lucide-react'

import { Link, useLocation } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'
import { useState } from 'react'

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar'
import { useSidebar } from '@/components/ui/sidebar-context'

interface NavSection {
  title: string
  url: string
  icon?: LucideIcon
  isActive?: boolean
  items?: {
    title: string
    url: string
    icon?: LucideIcon
  }[]
}

export function NavCollapsible({ items }: { items: NavSection[] }) {
  const { pathname } = useLocation()
  const { isMobile, setOpenMobile } = useSidebar()
  const handleMenuItemClick = () => {
    // Close mobile sidebar when a menu item is clicked on mobile
    if (isMobile) {
      setOpenMobile(false)
    }
  }

  return (
    <SidebarGroup>
      {/* <SidebarGroupLabel>Settings</SidebarGroupLabel> */}
      <SidebarMenu>
        {items.map(item => (
          <NavCollapsibleSection
            key={item.title}
            item={item}
            pathname={pathname}
            onItemSelect={handleMenuItemClick}
          />
        ))}
      </SidebarMenu>
    </SidebarGroup>
  )
}

function isSectionActive(item: NavSection, pathname: string) {
  return item.items?.some((subItem) => {
    if (!subItem.url)
      return false
    return pathname === subItem.url || pathname.startsWith(`${subItem.url}/`)
  }) ?? false
}

function NavCollapsibleSection({
  item,
  pathname,
  onItemSelect,
}: {
  item: NavSection
  pathname: string
  onItemSelect: () => void
}) {
  const sectionActive = isSectionActive(item, pathname)
  const [open, setOpen] = useState(item.isActive ?? sectionActive)
  const isOpen = sectionActive || open

  return (
    <Collapsible
      asChild
      open={isOpen}
      onOpenChange={setOpen}
      className="group/collapsible"
    >
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton tooltip={item.title}>
            {item.icon && <item.icon />}
            <span>{item.title}</span>
            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {item.items?.map(subItem => (
              <SidebarMenuSubItem key={subItem.title}>
                <SidebarMenuSubButton
                  asChild
                  isActive={
                    pathname === subItem.url
                    || pathname.startsWith(`${subItem.url}/`)
                  }
                >
                  <Link to={subItem.url} onClick={onItemSelect}>
                    {subItem.icon && <subItem.icon />}
                    <span>{subItem.title}</span>
                  </Link>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}
